import { useState } from "react";
import type {
  InvoiceFieldValidationModel,
  InvoiceRowValidationModel,
  InvoiceValidationDeterminationRuleModel,
} from "../../../models/InvoiceModels";
import styled from "styled-components";
import { theme } from "../../../theme";
import CircleIcon from "../../atoms/CircleIcon";
import Body from "../../atoms/typography/Body";
import { useTranslation } from "react-i18next";
import BodySmall from "../../atoms/typography/BodySmall";
import XS from "../../atoms/typography/XS";
import { SlideOutHeight } from "../../atoms/SlideOutHeight";
import { HorizontalSpacer } from "../../atoms/Spacers";
import { Row } from "react-grid-system";
import DetailsButton from "../../atoms/buttons/DetailsButton";
import {
  ChevronSmallDownIcon,
  ChevronSmallUpIcon,
} from "../../atoms/icons/ChevronIcons";

interface Props {
  fieldValidations?: Array<InvoiceFieldValidationModel>;
  rowFieldValidations?: Array<InvoiceRowValidationModel>;
  rules?: Array<InvoiceValidationDeterminationRuleModel>;
  externalErrorMessage?: string;
}

export const CheckItemContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 8px 0;

  border-bottom: 1px solid ${theme.colors.grey05};
`;

const InvoiceValidationResult: React.FC<Props> = ({
  fieldValidations,
  rowFieldValidations,
  rules,
  externalErrorMessage,
}) => {
  const { t } = useTranslation();
  const [displayApprovedValidationRules, setDisplayApprovedValidationRules] =
    useState(false);

  const filedValidationsThatAreNotValid = fieldValidations?.filter(
    (fieldValidation) => !fieldValidation.isValid,
  );

  const rowFieldValidationsThatAreNotValid =
    rowFieldValidations?.flatMap((rowValidation) =>
      rowValidation.rowFieldValidations.filter((fieldValidation) => {
        if (!fieldValidation.isValid) {
          return true;
        }
        return false;
      }),
    ) ?? [];

  const fieldValidationsThatAreValid = fieldValidations?.filter(
    (fieldValidation) => fieldValidation.isValid,
  );

  const rulesThatPassedWithWarningTrue = rules?.filter(
    (rule) => rule.passed === true && rule.warning,
  );
  const rulesThatPassedWithWarningFalse = rules?.filter(
    (rule) => rule.passed === true && !rule.warning,
  );
  const rulesThatDidNotPass = rules?.filter((rule) => rule.passed === false);

  return (
    <div>
      {externalErrorMessage && (
        <CheckItemContainer>
          <CircleIcon type="info" />
          <div>
            <XS fontweight={400} color={theme.colors.textMuted}>
              {t("invoiceValidation.externalErrorMessage").toUpperCase()}
            </XS>
            <BodySmall>{externalErrorMessage}</BodySmall>
          </div>
        </CheckItemContainer>
      )}
      {filedValidationsThatAreNotValid?.map((failedFieldValidation, index) => (
        <CheckItemContainer key={index}>
          <CircleIcon type="failed" />
          <div>
            <Body fontweight="600">
              {t("invoices.fieldIsInvalid", {
                fieldName: t(`invoices.${failedFieldValidation.fieldType}`, {
                  defaultValue: failedFieldValidation.fieldType,
                }),
              })}
            </Body>
            <BodySmall>{failedFieldValidation.value}</BodySmall>
          </div>
        </CheckItemContainer>
      ))}
      {rowFieldValidationsThatAreNotValid?.map((failedRowField, index) => (
        <CheckItemContainer key={index}>
          <CircleIcon type="failed" />
          <div>
            <Body fontweight="600">
              {t("invoices.rowIsInvalid", {
                rowName: t(`invoices.${failedRowField.fieldType}`, {
                  defaultValue: failedRowField.fieldType,
                }),
              })}
            </Body>
            <BodySmall>{failedRowField.value}</BodySmall>
          </div>
        </CheckItemContainer>
      ))}
      {rulesThatDidNotPass?.map((rule, index) => (
        <CheckItemContainer key={index}>
          <CircleIcon type="failed" />
          <div>
            <XS fontweight={400} color={theme.colors.textMuted}>
              {t("invoiceValidation.rule").toUpperCase()}
            </XS>
            <Body fontweight="600">{rule.ruleName}</Body>
            <BodySmall>{rule.message}</BodySmall>
          </div>
        </CheckItemContainer>
      ))}
      {rulesThatPassedWithWarningTrue?.map((rule, index) => (
        <CheckItemContainer key={index}>
          <CircleIcon type="warning" />
          <div>
            <XS fontweight={400} color={theme.colors.textMuted}>
              {t("invoiceValidation.rule").toUpperCase()}
            </XS>
            <Body fontweight="600">{rule.ruleName}</Body>
            <BodySmall>{rule.message}</BodySmall>
          </div>
        </CheckItemContainer>
      ))}
      {filedValidationsThatAreNotValid?.length === 0 &&
        rowFieldValidationsThatAreNotValid?.length === 0 &&
        rulesThatDidNotPass?.length === 0 &&
        rulesThatPassedWithWarningTrue?.length === 0 &&
        !externalErrorMessage && <Body>{t("invoices.allFieldsAreValid")}</Body>}
      <SlideOutHeight isVisible={displayApprovedValidationRules}>
        {rulesThatPassedWithWarningFalse?.map((rule, index) => (
          <CheckItemContainer key={index}>
            <CircleIcon type="success" />
            <div>
              <XS fontweight={400} color={theme.colors.textMuted}>
                {t("invoiceValidation.rule").toUpperCase()}
              </XS>
              <Body fontweight="600">{rule.ruleName}</Body>
              <BodySmall>{rule.message}</BodySmall>
            </div>
          </CheckItemContainer>
        ))}
        {fieldValidationsThatAreValid?.map((failedFieldValidation, index) => (
          <CheckItemContainer key={index}>
            <CircleIcon type="success" />
            <div>
              <Body fontweight="600">
                {t("invoices.fieldIsValid", {
                  fieldName: t(`invoices.${failedFieldValidation.fieldType}`, {
                    defaultValue: failedFieldValidation.fieldType,
                  }),
                })}
              </Body>

              <BodySmall>{failedFieldValidation.value}</BodySmall>
            </div>
          </CheckItemContainer>
        ))}
      </SlideOutHeight>
      <HorizontalSpacer spacing={0.5} />
      <Row nogutter justify="end">
        <DetailsButton
          color={theme.colors.primary}
          icon={
            displayApprovedValidationRules ? (
              <ChevronSmallUpIcon />
            ) : (
              <ChevronSmallDownIcon />
            )
          }
          title={
            displayApprovedValidationRules
              ? t("invoices.hideApproved")
              : t("invoices.displayApproved")
          }
          onClick={() =>
            setDisplayApprovedValidationRules(!displayApprovedValidationRules)
          }
        />
      </Row>
    </div>
  );
};

export default InvoiceValidationResult;
